/**
 * Main Application Component
 *
 * This component defines the application's routing structure.
 * It uses React Router v6 for client-side routing.
 *
 * Route structure:
 * - Public routes: /login, /register (accessible without authentication)
 * - Protected routes: Everything else (requires authentication)
 *
 * The ProtectedRoute component handles redirection for unauthenticated users.
 */

import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Import page components
import Login from './pages/Login';
import Register from './pages/Register';
import POIngest from './pages/POIngest';
import DocumentReviewer from './pages/DocumentReviewer';
import POList from './pages/POList';
import PODetail from './pages/PODetail';
import AdminSettings from './pages/AdminSettings';
import DocumentQueue from './pages/DocumentQueue';
import SystemAdmin from './pages/SystemAdmin';
import CompanyList from './pages/CompanyList';
import CompanyDetail from './pages/CompanyDetail';
import AuditLog from './pages/AuditLog';
import PartnerList from './pages/admin/PartnerList';
import PartnerDetail from './pages/admin/PartnerDetail';
import OceanBookingList from './pages/OceanBookingList';
import OceanBookingDetail from './pages/OceanBookingDetail';
import BillOfLadingList from './pages/BillOfLadingList';
import BillOfLadingDetail from './pages/BillOfLadingDetail';

// Import layout components
import MainLayout from './components/layout/MainLayout';

/**
 * ProtectedRoute Component
 *
 * A wrapper component that checks if the user is authenticated.
 * If not authenticated, redirects to the login page.
 * If authenticated, renders the children components.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 */
function ProtectedRoute({ children }) {
  // Get authentication state from context
  const { user, loading } = useAuth();

  // Show nothing while checking authentication status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Render children if authenticated
  return children;
}

/**
 * DefaultRedirect Component
 * Sends super admins to /system-admin, everyone else to /purchase-orders
 */
function DefaultRedirect() {
  const { user } = useAuth();
  if (user?.role === 'super_admin') {
    return <Navigate to="/system-admin" replace />;
  }
  return <Navigate to="/purchase-orders" replace />;
}

/**
 * ProtectedLayout Component
 * Wraps MainLayout with authentication check
 */
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  );
}

/**
 * Router Configuration
 * Using createBrowserRouter for data router features (useBlocker, etc.)
 */
const router = createBrowserRouter([
  // Public Routes - Accessible without authentication
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  // Protected Routes - Require authentication
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      // Redirect root based on role
      {
        index: true,
        element: <DefaultRedirect />,
      },
      // Purchase Order Routes
      {
        path: 'purchase-orders',
        element: <Outlet />,
        children: [
          // List all POs
          {
            index: true,
            element: <POList />,
          },
          // Ingest new PO (upload document) - must come before :id
          {
            path: 'ingest',
            element: <POIngest />,
          },
          // View/Edit single PO - dynamic route must come last
          {
            path: ':id',
            element: <PODetail />,
          },
        ],
      },
      // Ocean Booking Routes
      {
        path: 'ocean-bookings',
        element: <Outlet />,
        children: [
          { index: true, element: <OceanBookingList /> },
          { path: ':id', element: <OceanBookingDetail /> },
        ],
      },
      // Bill of Lading Routes
      {
        path: 'bills-of-lading',
        element: <Outlet />,
        children: [
          { index: true, element: <BillOfLadingList /> },
          { path: ':id', element: <BillOfLadingDetail /> },
        ],
      },
      // Review AI-extracted data (HITL interface)
      {
        path: 'review',
        element: <DocumentReviewer />,
      },
      // Document Queue
      {
        path: 'document-queue',
        element: <DocumentQueue />,
      },
      // Admin Settings (admin only)
      {
        path: 'settings',
        element: <AdminSettings />,
      },
      // System Admin (super_admin only)
      {
        path: 'system-admin',
        element: <SystemAdmin />,
      },
      // Company management (super_admin only)
      {
        path: 'companies',
        element: <Outlet />,
        children: [
          { index: true, element: <CompanyList /> },
          { path: ':id', element: <CompanyDetail /> },
        ],
      },
      // Audit Log (super_admin only)
      {
        path: 'admin/audit-log',
        element: <AuditLog />,
      },
      // Partner Master (admin only)
      {
        path: 'admin/partners',
        element: <PartnerList />,
      },
      {
        path: 'admin/partners/:id',
        element: <PartnerDetail />,
      },
    ],
  },
  // Catch-all route - Redirect unknown paths to dashboard
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

/**
 * App Component
 *
 * The root component that provides the router.
 */
function App() {
  return <RouterProvider router={router} />;
}

export default App;
